/**
 * Admin Service
 * API calls for admin dashboard functionality
 */

import { API_BASE_URL } from '../config/api';

const getAuthHeaders = () => {
  const token = localStorage.getItem('account-token');
  return {
    'Content-Type': 'application/json',
    'Authorization': token ? `Bearer ${token}` : ''
  };
};

// Analytics Types
export interface AnalyticsSummary {
  totalUsers: number;
  newUsersThisWeek: number;
  activeSubscriptions: number;
  revenueThisMonth: number;
  totalKBArticles: number;
  pendingVerifications: number;
}

export interface UserDataPoint {
  date: string;
  count: number;
}

export interface RevenueDataPoint {
  date: string;
  amount: number;
  count: number;
}

export interface UserAnalytics {
  period: string;
  dataPoints: UserDataPoint[];
  total: number;
}

export interface RevenueAnalytics {
  period: string;
  dataPoints: RevenueDataPoint[];
  total: number;
  currency: string;
}

// User Management Types
export interface AdminUser {
  id: string;
  email: string;
  name: string;
  joinDate: string;
  emailVerified: boolean;
  isAdmin: boolean;
  isLocked?: boolean;
  lockedUntil?: string;
  failedLoginAttempts?: number;
}

export interface UserPagination {
  currentPage: number;
  totalPages: number;
  totalUsers: number;
  limit: number;
  hasNext: boolean;
  hasPrev: boolean;
}

export interface UsersResponse {
  users: AdminUser[];
  pagination: UserPagination;
}

// KB Article Types
export interface KBCategory {
  id: string;
  name: string;
  description?: string;
  sortOrder: number;
}

export interface KBArticle {
  id: string;
  title: string;
  slug: string;
  content: string;
  frames: string[];
  tags: string[];
  isPublished: boolean;
  sortOrder: number;
  categoryId?: string;
  categoryName?: string;
  createdAt: string;
  updatedAt: string;
}

// Analytics API
export const analyticsApi = {
  async getSummary(): Promise<AnalyticsSummary> {
    const response = await fetch(`${API_BASE_URL}/api/admin/analytics/summary`, {
      headers: getAuthHeaders()
    });
    
    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Admin access denied');
      }
      throw new Error('Failed to fetch analytics summary');
    }
    
    const data = await response.json();
    return data.data;
  },

  async getUserAnalytics(params: { period: '7d' | '30d' | '1y' } | { startDate: string; endDate: string }): Promise<UserAnalytics> {
    const queryParams = 'period' in params 
      ? `period=${params.period}`
      : `startDate=${params.startDate}&endDate=${params.endDate}`;
    
    const response = await fetch(`${API_BASE_URL}/api/admin/analytics/users?${queryParams}`, {
      headers: getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch user analytics');
    }
    
    const data = await response.json();
    return data.data;
  },

  async getRevenueAnalytics(params: { period: '7d' | '30d' | '1y' } | { startDate: string; endDate: string }): Promise<RevenueAnalytics> {
    const queryParams = 'period' in params 
      ? `period=${params.period}`
      : `startDate=${params.startDate}&endDate=${params.endDate}`;
    
    const response = await fetch(`${API_BASE_URL}/api/admin/analytics/revenue?${queryParams}`, {
      headers: getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch revenue analytics');
    }
    
    const data = await response.json();
    return data.data;
  }
};

// User Management API
export const usersApi = {
  async getUsers(
    page = 1, 
    limit: 25 | 50 | 100 | 200 = 25, 
    search = '', 
    filter = ''
  ): Promise<UsersResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      ...(search && { search }),
      ...(filter && { filter })
    });

    const response = await fetch(`${API_BASE_URL}/api/admin/users?${params}`, {
      headers: getAuthHeaders()
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to fetch users');
    }
    
    const data = await response.json();
    
    // Handle both wrapped ({ data: { users } }) and direct ({ users }) response formats
    const users = data.data?.users || data.users || [];
    
    // Apply client-side filtering if backend doesn't support it
    let filteredUsers = users;
    if (search) {
      const searchLower = search.toLowerCase();
      filteredUsers = users.filter((u: AdminUser) => 
        u.email?.toLowerCase().includes(searchLower) || 
        u.name?.toLowerCase().includes(searchLower)
      );
    }
    if (filter === 'verified') {
      filteredUsers = filteredUsers.filter((u: AdminUser) => u.emailVerified);
    } else if (filter === 'unverified') {
      filteredUsers = filteredUsers.filter((u: AdminUser) => !u.emailVerified);
    } else if (filter === 'locked') {
      filteredUsers = filteredUsers.filter((u: AdminUser) => u.isLocked || (u.lockedUntil && new Date(u.lockedUntil) > new Date()));
    }
    
    // Apply client-side pagination
    const startIndex = (page - 1) * limit;
    const paginatedUsers = filteredUsers.slice(startIndex, startIndex + limit);
    
    return {
      users: paginatedUsers,
      pagination: data.data?.pagination || data.pagination || {
        total: filteredUsers.length,
        page,
        limit,
        pages: Math.ceil(filteredUsers.length / limit)
      }
    };
  },

  async updateEmail(userId: string, email: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/admin/users/${userId}/email`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ email })
    });
    
    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.message || 'Failed to update email');
    }
  },

  async resendVerification(userId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/admin/users/${userId}/resend-verification`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error('Failed to resend verification email');
    }
  },

  async deleteUser(userId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/admin/users/${userId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
      body: JSON.stringify({ confirmation: 'delete' })
    });
    
    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.message || 'Failed to delete user');
    }
  },

  async unlockUser(userId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/admin/users/${userId}/unlock`, {
      method: 'POST',
      headers: getAuthHeaders()
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.message || 'Failed to unlock user');
    }
  },

  async setAdminStatus(userId: string, isAdmin: boolean): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/admin/users/${userId}/admin`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ isAdmin })
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.message || 'Failed to update admin status');
    }
  }
};

// KB Articles API
export const kbArticlesApi = {
  async getArticles(sort: 'frame' | 'alpha' = 'alpha'): Promise<KBArticle[]> {
    const response = await fetch(`${API_BASE_URL}/api/admin/kb-articles?sort=${sort}`, {
      headers: getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch KB articles');
    }
    
    const data = await response.json();
    return data.data;
  },

  async getArticle(id: string): Promise<KBArticle> {
    const response = await fetch(`${API_BASE_URL}/api/admin/kb-articles/${id}`, {
      headers: getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch KB article');
    }
    
    const data = await response.json();
    return data.data;
  },

  async createArticle(article: {
    title: string;
    content: string;
    categoryId?: string;
    frames?: string[];
    tags?: string[];
    isPublished?: boolean;
  }): Promise<KBArticle> {
    const response = await fetch(`${API_BASE_URL}/api/admin/kb-articles`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(article)
    });
    
    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.message || 'Failed to create article');
    }
    
    const data = await response.json();
    return data.data;
  },

  async updateArticle(id: string, updates: Partial<{
    title: string;
    content: string;
    categoryId: string;
    frames: string[];
    tags: string[];
    isPublished: boolean;
    sortOrder: number;
  }>): Promise<KBArticle> {
    const response = await fetch(`${API_BASE_URL}/api/admin/kb-articles/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(updates)
    });
    
    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.message || 'Failed to update article');
    }
    
    const data = await response.json();
    return data.data;
  },

  async deleteArticle(id: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/admin/kb-articles/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error('Failed to delete article');
    }
  },

  async getCategories(): Promise<KBCategory[]> {
    const response = await fetch(`${API_BASE_URL}/api/admin/kb-articles/categories/list`, {
      headers: getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch categories');
    }
    
    const data = await response.json();
    return data.data;
  },

  async createCategory(name: string, description?: string): Promise<KBCategory> {
    const response = await fetch(`${API_BASE_URL}/api/admin/kb-articles/categories`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ name, description })
    });
    
    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.message || 'Failed to create category');
    }
    
    const data = await response.json();
    return data.data;
  },

  async exportAllArticles(): Promise<{
    metadata: {
      exportDate: string;
      sourceEnvironment: string;
      articleCount: number;
      categoryCount: number;
      exportedBy: string;
    };
    categories: KBCategory[];
    articles: KBArticle[];
  }> {
    const response = await fetch(`${API_BASE_URL}/api/admin/kb-articles/export/all`, {
      headers: getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error('Failed to export KB articles');
    }
    
    const data = await response.json();
    return data.data;
  },

  async importAllArticles(exportData: {
    categories: KBCategory[];
    articles: KBArticle[];
  }): Promise<{
    summary: {
      categories: { imported: number; skipped: number; total: number };
      articles: { imported: number; skipped: number; total: number };
    };
  }> {
    const response = await fetch(`${API_BASE_URL}/api/admin/kb-articles/import/all`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(exportData)
    });
    
    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.message || 'Failed to import KB articles');
    }
    
    const data = await response.json();
    return data;
  }
};

// Admin Auth Check
export const checkAdminAccess = async (): Promise<boolean> => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/admin/analytics/summary`, {
      headers: getAuthHeaders()
    });
    return response.ok;
  } catch {
    return false;
  }
};

// Geo-Blocking Types
export interface GeoBlockingConfig {
  enabled: boolean;
  allowedCountries: string[];
  lastUpdated: string;
  updatedBy: string | null;
  requiresRestart: boolean;
  implementation: string;
  note: string;
}

export interface GeoBlockingStats {
  totalBlocked: number;
  totalAllowed: number;
  blockedByCountry: Record<string, number>;
  recentBlocks: Array<{
    timestamp: string;
    country: string;
    ip: string;
    path: string;
    userAgent: string | null;
  }>;
  period: string;
}

export interface IPTestResult {
  ip: string;
  country: string | null;
  blocked: boolean;
  city?: string | null;
  region?: string | null;
  timezone?: string | null;
  message: string;
}

// Geo-Blocking API
export const geoBlockingApi = {
  async getConfig(): Promise<{ success: boolean; config: GeoBlockingConfig }> {
    const response = await fetch(`${API_BASE_URL}/api/admin/geo-blocking`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch geo-blocking config');
    return response.json();
  },

  async getStats(): Promise<{ success: boolean; stats: GeoBlockingStats }> {
    const response = await fetch(`${API_BASE_URL}/api/admin/geo-blocking/stats`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch geo-blocking stats');
    return response.json();
  },

  async testIP(ip: string): Promise<{ success: boolean; result: IPTestResult }> {
    const response = await fetch(`${API_BASE_URL}/api/admin/geo-blocking/test-ip`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ ip })
    });
    if (!response.ok) throw new Error('Failed to test IP');
    return response.json();
  }
};
